import React, { useState } from 'react'

import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import CheckBox from '../../controls/CheckBox'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'

import './MedicationsSection.scss'

export default {
  name: 'Medications/Allergies',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

// shamelessly using function hoisting for code clarity

function getValidationSchema() {
  return validation.object({
    medications: validation
      .arrayOf(
        validation.object({
          name: validation.name('Medication Name').required(),
          dosage: validation.shortText('Dosage'),
          frequency: validation.shortText('Frequency'),
          reason: validation.shortText('Reason'),
          dateStarted: validation.date('Date Started').nullable(),
          dateDiscontinued: validation.date('Date Discontinued').nullable(),
          pharmacy: validation.shortText('Pharmacy'),
          relatedToInjury: validation.boolean().nullable(),
          preInjury: validation.boolean().nullable(),
          isPreInjury: validation.boolean().nullable(),
          discontinued: validation.boolean().nullable(),
          isDiscontinued: validation.boolean().nullable(),
        }),
      )
      .nullable(),
    allergies: validation
      .arrayOf(
        validation.object({
          name: validation.name('Allergy Name').required(),
          reaction: validation.shortText('Reaction'),
        }),
      )
      .nullable(),
  })
}

function Component({ addItemToArray, removeItemFromArray, createInputProps, getValue, ErrorMessage }) {
  const [editingMedicationIndex, setEditingMedicationIndex] = useState(null)
  const [editingAllergyIndex, setEditingAllergyIndex] = useState(null)

  const medications = getValue('medications', [])
  const allergies = getValue('allergies', [])

  const handleAddMedication = () => {
    const nextIndex = medications.length
    addItemToArray('medications', {})
    setEditingMedicationIndex(nextIndex)
  }

  const handleRemoveMedication = index => {
    removeItemFromArray('medications', index)
    if (editingMedicationIndex === index) {
      setEditingMedicationIndex(null)
    } else if (editingMedicationIndex !== null && editingMedicationIndex > index) {
      setEditingMedicationIndex(editingMedicationIndex - 1)
    }
  }

  const handleAddAllergy = () => {
    const nextIndex = allergies.length
    addItemToArray('allergies', {})
    setEditingAllergyIndex(nextIndex)
  }

  const handleRemoveAllergy = index => {
    removeItemFromArray('allergies', index)
    if (editingAllergyIndex === index) {
      setEditingAllergyIndex(null)
    } else if (editingAllergyIndex !== null && editingAllergyIndex > index) {
      setEditingAllergyIndex(editingAllergyIndex - 1)
    }
  }

  return (
    <div className="MedicationsSection-component">
      <h4 className="pb-3">Medications</h4>
      {medications.map((x, index) => {
        const namePrefix = `medications[${index}]`
        const isEditing = editingMedicationIndex === index
        return (
          <React.Fragment key={namePrefix}>
            <Medication
              namePrefix={namePrefix}
              createInputProps={createInputProps}
              getValue={getValue}
              ErrorMessage={ErrorMessage}
              isEditing={isEditing}
              onEdit={() => setEditingMedicationIndex(index)}
              onDone={() => setEditingMedicationIndex(null)}
              handleRemove={() => handleRemoveMedication(index)}
            />
            <hr />
          </React.Fragment>
        )
      })}
      <AddButton id="addMedicationButton" onClick={handleAddMedication}>
        Add Medication
      </AddButton>

      <h4 className="pb-3 mt-5">Allergies</h4>
      {allergies.map((x, index) => {
        const namePrefix = `allergies[${index}]`
        const isEditing = editingAllergyIndex === index
        return (
          <React.Fragment key={namePrefix}>
            <Allergy
              key={namePrefix}
              namePrefix={namePrefix}
              createInputProps={createInputProps}
              getValue={getValue}
              ErrorMessage={ErrorMessage}
              isEditing={isEditing}
              onEdit={() => setEditingAllergyIndex(index)}
              onDone={() => setEditingAllergyIndex(null)}
              handleRemove={() => handleRemoveAllergy(index)}
            />
            <hr />
          </React.Fragment>
        )
      })}
      <AddButton id="addAllergyButton" onClick={handleAddAllergy}>
        Add Allergy
      </AddButton>
    </div>
  )
}

function Medication({ namePrefix, createInputProps, ErrorMessage, handleRemove, getValue, isEditing, onEdit, onDone }) {
  const medication = getValue(namePrefix, {})
  const name = medication?.name || ''
  const dosage = medication?.dosage || ''
  const frequency = medication?.frequency || ''
  const reason = medication?.reason || ''
  const dateStarted = medication?.dateStarted || ''
  const dateDiscontinued = medication?.dateDiscontinued || ''
  const pharmacy = medication?.pharmacy || ''
  const relatedToInjury = medication?.relatedToInjury
  const preInjury = Boolean(medication?.preInjury || medication?.isPreInjury)
  const discontinued = Boolean(medication?.discontinued || medication?.isDiscontinued || dateDiscontinued)

  const dosageFreq = [dosage, frequency].filter(Boolean).join(' • ')

  if (!isEditing) {
    return (
      <div
        className={`d-flex align-items-start justify-content-between clearfix ${discontinued ? 'discontinued' : ''}`}
      >
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{name || <em className="text-muted">Untitled Medication</em>}</span>
            {dosageFreq && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                {dosageFreq}
              </span>
            )}
            {discontinued ? (
              <span className="badge badge-secondary ml-3">
                Discontinued{dateDiscontinued ? `: ${dateDiscontinued}` : ''}
              </span>
            ) : dateStarted ? (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                Started: {dateStarted}
              </span>
            ) : null}
            {preInjury && <span className="badge badge-warning text-dark ml-3">Pre-injury</span>}
            {relatedToInjury && <span className="badge badge-info ml-3">Related to Injury</span>}
          </div>
          {(reason || pharmacy) && (
            <div className="text-muted mt-1" style={{ fontSize: '0.85em' }}>
              {reason && <span>Reason: {reason}</span>}
              {reason && pharmacy && <span> • </span>}
              {pharmacy && <span>Pharmacy: {pharmacy}</span>}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit} title="Edit this medication">
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`${namePrefix}RemoveBtn`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className={`clearfix ${discontinued ? 'discontinued' : ''}`}>
      <div className="form-row">
        <div className="form-group col-md-6">
          <label className="form-label" htmlFor={`${namePrefix}.name`}>
            Medication Name
          </label>
          <input
            type="text"
            className="form-control"
            maxLength={validation.maxLengths.name}
            {...createInputProps(`${namePrefix}.name`)}
          />
        </div>
        <div className="form-group col-md-3">
          <label className="form-label" htmlFor={`${namePrefix}.dosage`}>
            Dosage
          </label>
          <input
            type="text"
            className="form-control"
            maxLength={validation.maxLengths.shortText}
            {...createInputProps(`${namePrefix}.dosage`)}
          />
        </div>
        <div className="form-group col-md-3">
          <label className="form-label" htmlFor={`${namePrefix}.frequency`}>
            Frequency
          </label>
          <input
            type="text"
            className="form-control"
            maxLength={validation.maxLengths.shortText}
            {...createInputProps(`${namePrefix}.frequency`)}
          />
        </div>
        <ErrorMessage name={`${namePrefix}.name`} />
        <ErrorMessage name={`${namePrefix}.dosage`} />
        <ErrorMessage name={`${namePrefix}.frequency`} />
      </div>
      <div className="form-row">
        <div className="form-group col-md-6">
          <label className="form-label" htmlFor={`${namePrefix}.reason`}>
            Reason
          </label>
          <input
            type="text"
            className="form-control"
            maxLength={validation.maxLengths.shortText}
            {...createInputProps(`${namePrefix}.reason`)}
          />
        </div>
        <div className="form-group col-md-3">
          <label className="form-label" htmlFor={`${namePrefix}.dateStarted`}>
            Date Started
          </label>
          <DatePicker {...createInputProps(`${namePrefix}.dateStarted`)} />
        </div>
        <div className="form-group col-md-3">
          <label className="form-label" htmlFor={`${namePrefix}.dateDiscontinued`}>
            Date Discontinued
          </label>
          <DatePicker {...createInputProps(`${namePrefix}.dateDiscontinued`)} />
        </div>
        <ErrorMessage name={`${namePrefix}.reason`} />
        <ErrorMessage name={`${namePrefix}.dateStarted`} />
        <ErrorMessage name={`${namePrefix}.dateDiscontinued`} />
      </div>
      <div className="form-row">
        <div className="form-group col-md-3">
          <label className="form-label" htmlFor={`${namePrefix}.pharmacy`}>
            Pharmacy
          </label>
          <input
            type="text"
            className="form-control"
            maxLength={validation.maxLengths.shortText}
            {...createInputProps(`${namePrefix}.pharmacy`)}
          />
        </div>
        <div className="form-group align-center mt-4 col-4 col-md-3">
          <CheckBox {...createInputProps(`${namePrefix}.relatedToInjury`)}>Related to Injury</CheckBox>
        </div>
        <div className="form-group align-center mt-4 col-4 col-md-3">
          <CheckBox {...createInputProps(`${namePrefix}.preInjury`)}>Pre-injury</CheckBox>
        </div>
        <div className="form-group align-center mt-4 col-4 col-md-3">
          <CheckBox {...createInputProps(`${namePrefix}.discontinued`)}>Discontinued</CheckBox>
        </div>
        <ErrorMessage name={`${namePrefix}.pharmacy`} />
      </div>
      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`${namePrefix}RemoveBtn`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}

function Allergy({ namePrefix, createInputProps, ErrorMessage, handleRemove, getValue, isEditing, onEdit, onDone }) {
  const allergy = getValue(namePrefix, {})
  const name = allergy?.name || ''
  const reaction = allergy?.reaction || ''

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{name || <em className="text-muted">Untitled Allergy</em>}</span>
            {reaction && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                Reaction: {reaction}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit} title="Edit this allergy">
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`${namePrefix}RemoveBtn`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="allergy clearfix">
      <div className="form-row">
        <div className="form-group col-md-6">
          <label htmlFor={`${namePrefix}.name`} className="form-label">
            Allergy Name
          </label>
          <input
            type="text"
            id={`${namePrefix}.name`}
            maxLength={validation.maxLengths.name}
            className="form-control"
            {...createInputProps(`${namePrefix}.name`)}
          />
          <ErrorMessage name={`${namePrefix}.name`} />
        </div>
        <div className="form-group col-md-6">
          <label htmlFor={`${namePrefix}.reaction`} className="form-label">
            Reaction
          </label>
          <input
            type="text"
            id={`${namePrefix}.reaction`}
            maxLength={validation.maxLengths.shortText}
            className="form-control allergy-reaction"
            {...createInputProps(`${namePrefix}.reaction`)}
          />
          <ErrorMessage name={`${namePrefix}.reaction`} />
        </div>
      </div>
      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`${namePrefix}RemoveBtn`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
